import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSessionContext } from '../context/SessionContext';
import { useGoalsContext } from '../context/GoalsContext';
import { useFitnessContext } from '../context/FitnessContext';
import { useGamificationContext } from '../context/GamificationContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Textarea from '../components/ui/Textarea';
import Label from '../components/ui/Label';
import { 
  FiArrowLeft, 
  FiDownload, 
  FiUpload, 
  FiTrash2, 
  FiAlertTriangle, 
  FiCheckCircle, 
  FiDatabase, 
  FiCopy,
  FiEye,
  FiEyeOff,
  FiInfo,
  FiSave,
  FiAward
} from 'react-icons/fi';

const Settings = () => {
  const { sessions } = useSessionContext();
  const { overallGoals, sessionGoals } = useGoalsContext();
  const { waterIntakeHistory } = useFitnessContext();
  const { gamificationEnabled, setGamificationEnabled, userStats, unlockedAchievements } = useGamificationContext();
  
  const [exportStatus, setExportStatus] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [showRawData, setShowRawData] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);

  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (downloadLink?.url) {
        URL.revokeObjectURL(downloadLink.url);
      }
    };
  }, [downloadLink]);

  const dataStats = {
    sessions: sessions.length,
    overallGoals: overallGoals.length,
    sessionGoals: sessionGoals.length,
    hydrationRecords: waterIntakeHistory.length,
    totalRecords: sessions.length + overallGoals.length + sessionGoals.length + waterIntakeHistory.length
  };

  const generateExportData = () => {
    // 1. Calculate Analytics for the Backup
    const totalSessions = sessions.length;
    const totalMatTime = sessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const averageSessionLength = totalSessions > 0 ? Math.round(totalMatTime / totalSessions) : 0;
    const totalWins = sessions.reduce((sum, s) => sum + (s.wins || 0), 0);
    const totalLosses = sessions.reduce((sum, s) => sum + (s.losses || 0), 0);
    const winRate = totalWins + totalLosses > 0 ? Math.round((totalWins / (totalWins + totalLosses)) * 100) : 0;
    
    // Submissions Analytics
    const totalSubmissions = sessions.reduce((sum, s) =>
        sum + (s.techniques?.filter((t) => 
          t.outcome === 'Succeeded' && 
          ['choke', 'lock', 'bar'].some(term => t.name.toLowerCase().includes(term))
        ).length || 0), 0);
    const submissionsPerSession = totalSessions > 0 ? Number((totalSubmissions / totalSessions).toFixed(1)) : 0;

    return {
      exportDate: new Date().toISOString(),
      version: '1.1.0',
      data: {
        sessions,
        overallGoals,
        sessionGoals,
        waterIntakeHistory
      },
      analytics: {
        totalSessions,
        totalMatTimeMinutes: totalMatTime,
        averageSessionLengthMinutes: averageSessionLength,
        totalWins,
        totalLosses,
        winRatePercent: winRate,
        totalSubmissions,
        submissionsPerSession
      },
      gamification: {
        enabled: gamificationEnabled,
        level: userStats?.level || 1,
        xp: userStats?.xp || 0,
        unlockedAchievements: unlockedAchievements?.filter(a => a.unlocked) || []
      },
      metadata: {
        totalSessions: sessions.length,
        totalOverallGoals: overallGoals.length,
        totalSessionGoals: sessionGoals.length,
        totalHydrationRecords: waterIntakeHistory.length
      }
    };
  };

  const handleExportData = () => {
    // Revoke previous URL if it exists
    if (downloadLink?.url) {
      URL.revokeObjectURL(downloadLink.url);
    }
    setDownloadLink(null);
    setExportStatus({ type: 'info', message: 'Generating backup...' });

    try {
      const exportData = generateExportData();
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `clinchfirst-backup-${timestamp}.json`;
      
      // 1. Attempt automatic download via hidden link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup DOM element immediately, but keep URL valid for manual link
      document.body.removeChild(link);

      // 2. Set manual download link as fallback
      setDownloadLink({ url, filename });
      setExportStatus({ type: 'success', message: 'Backup generated successfully!' });
      
      // Clear success message after 5s, but keep the download link visible
      setTimeout(() => setExportStatus(prev => prev?.type === 'success' ? null : prev), 5000);
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus({ type: 'error', message: 'Failed to generate backup: ' + error.message });
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      const exportData = generateExportData();
      const jsonString = JSON.stringify(exportData, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setExportStatus({ type: 'success', message: 'Backup data copied to clipboard!' });
      setTimeout(() => setExportStatus(null), 5000);
    } catch (error) {
      setExportStatus({ type: 'error', message: 'Failed to copy to clipboard.' });
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target?.result);
        
        if (!importData.data || !importData.version) {
          throw new Error('Invalid backup file format');
        }

        if (!window.confirm(`This will replace your current data with the backup from ${new Date(importData.exportDate).toLocaleDateString()}.\n\nAre you sure?`)) {
          return;
        }

        // Note: For backend-connected apps, a true import requires API creation loops. 
        // We set local fallback items here to preserve legacy functionality.
        localStorage.setItem('clinchfirst_sessions', JSON.stringify(importData.data.sessions || []));
        localStorage.setItem('clinchfirst_overall_goals', JSON.stringify(importData.data.overallGoals || []));
        localStorage.setItem('clinchfirst_session_goals', JSON.stringify(importData.data.sessionGoals || []));
        localStorage.setItem('clinchfirst_water_intake', JSON.stringify(importData.data.waterIntakeHistory || []));
        
        setImportStatus({ type: 'success', message: 'Data imported! Refreshing...' });
        setTimeout(() => window.location.reload(), 1500);
      } catch (error) {
        setImportStatus({ type: 'error', message: 'Failed to import data. Check file format.' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleClearAllData = () => {
    if (window.confirm('⚠️ WARNING: This will permanently delete ALL your data including:\n\n• Training Sessions\n• Goals & Milestones\n• Hydration History\n• Achievements & Level Progress\n\nThis cannot be undone. Are you absolutely sure?')) {
      const keysToRemove = [
        'clinchfirst_sessions',
        'clinchfirst_overall_goals',
        'clinchfirst_session_goals',
        'clinchfirst_water_intake',
        'clinchfirst_water_goal',
        'clinchfirst_overall_goal_onboarding_completed'
      ];

      keysToRemove.forEach(key => localStorage.removeItem(key));
      localStorage.clear();
      window.location.href = '/'; 
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <Link to="/">
          <Button variant="ghost" size="sm" className="mb-4">
            <FiArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">Settings & Data</h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">
          Manage your data, export backups, and control your preferences.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <FiAward className="w-5 h-5 mr-2 text-yellow-600 dark:text-yellow-400" /> Application Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">Gamification</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">Enable levels, XP, and achievements to make training more fun.</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={gamificationEnabled}
                onChange={(e) => setGamificationEnabled(e.target.checked)}
              />
              <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6 border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center text-blue-900 dark:text-blue-100">
            <FiDatabase className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" /> Your Data Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{dataStats.sessions}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">Sessions</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{dataStats.overallGoals}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">Overall Goals</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{dataStats.sessionGoals}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">Session Goals</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{dataStats.hydrationRecords}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">Hydration Days</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <FiDownload className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" /> Export Your Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Create a portable backup of all your training history. This now includes a full summary of your generated analytics, total mat time, and gamification progress!
          </p>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleExportData} className="w-full sm:w-auto">
                <FiDownload className="w-4 h-4 mr-2" /> Generate Backup
              </Button>
              <Button onClick={handleCopyToClipboard} variant="outline" className="w-full sm:w-auto">
                <FiCopy className="w-4 h-4 mr-2" /> Copy to Clipboard
              </Button>
              <Button onClick={() => setShowRawData(!showRawData)} variant="outline" className="w-full sm:w-auto">
                {showRawData ? <FiEyeOff className="w-4 h-4 mr-2" /> : <FiEye className="w-4 h-4 mr-2" />}
                {showRawData ? 'Hide Raw Data' : 'View Raw Data'}
              </Button>
            </div>

            {/* Manual Download Link */}
            {downloadLink && (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg flex flex-col sm:flex-row items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center text-green-800 dark:text-green-100">
                  <FiCheckCircle className="w-5 h-5 mr-2" />
                  <div>
                    <p className="font-semibold text-sm">Backup Ready!</p>
                    <p className="text-xs text-green-700 dark:text-green-300">If the download didn't start, click the button.</p>
                  </div>
                </div>
                <a 
                  href={downloadLink.url} 
                  download={downloadLink.filename}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-green-300 dark:border-green-700 shadow-sm text-sm font-medium rounded-md text-green-700 dark:text-green-400 bg-white dark:bg-slate-800 hover:bg-green-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <FiSave className="w-4 h-4 mr-2" /> Save {downloadLink.filename}
                </a>
              </div>
            )}

            {showRawData && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-300 mt-2">
                <Label className="mb-2 block font-semibold text-slate-900 dark:text-white">Raw JSON Data</Label>
                <Textarea 
                  value={JSON.stringify(generateExportData(), null, 2)} 
                  readOnly 
                  className="font-mono text-xs h-64 bg-slate-50 dark:bg-slate-900 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white" 
                  onClick={(e) => e.target.select()}
                />
              </div>
            )}
          </div>
          
          {exportStatus && !downloadLink && (
            <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 border animate-in fade-in zoom-in-95 duration-200 ${
              exportStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50 text-green-800 dark:text-green-100' : 
              exportStatus.type === 'info' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50 text-blue-800 dark:text-blue-100' : 
              'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-800 dark:text-red-100'
            }`}>
              {exportStatus.type === 'success' ? <FiCheckCircle /> : exportStatus.type === 'info' ? <FiInfo /> : <FiAlertTriangle />}
              <p className="text-sm font-medium">{exportStatus.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <FiUpload className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" /> Import Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 rounded-lg p-3 mb-4 text-sm text-yellow-800 dark:text-yellow-100">
            <strong>Warning:</strong> Importing a backup will overwrite all existing data on this device.
          </div>
          <input 
            type="file" 
            accept=".json" 
            onChange={handleImportData} 
            className="hidden" 
            id="import-file" 
          />
          <label htmlFor="import-file" className="cursor-pointer">
            <div className="inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
              <FiUpload className="w-4 h-4 mr-2" /> Select Backup File
            </div>
          </label>
          
          {importStatus && (
            <div className={`mt-4 p-3 rounded-lg flex items-center space-x-2 border ${
              importStatus.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50 text-green-800 dark:text-green-100' 
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50 text-red-800 dark:text-red-100'
            }`}>
              <p className="text-sm font-medium">{importStatus.message}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20">
        <CardHeader>
          <CardTitle className="text-lg flex items-center text-red-900 dark:text-red-100">
            <FiAlertTriangle className="w-5 h-5 mr-2 text-red-600 dark:text-red-400" /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-700 dark:text-red-300 mb-4">
            Permanently delete all training sessions, goals, achievements, and records from this device.
          </p>
          <Button 
            variant="outline" 
            onClick={handleClearAllData} 
            className="border-red-300 dark:border-red-700/50 text-red-700 dark:text-red-400 hover:bg-red-600 dark:hover:bg-red-600 hover:text-white transition-colors"
          >
            <FiTrash2 className="w-4 h-4 mr-2" /> Reset All Data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;